import AppActions from '../actions/app';
import { pathSources } from '../constants/common';
import { getLinks } from '../utils/LocaleMap';
import { DEFAULT_POS } from '../utils/PosUtil';

const initialState = {
    rootPath: '',
    fallbackTranslations: {},
    translations: {},
    locale: {
        canonical: 'en_US',
        display: 'en_US',
        list: []
    },
    query: '',
    isPm: false,
    usesSoftware: false,
    answers: {
        softwareId: null,
        propertyCount: null,
        isSameBuilding: null,
        propertyType: []
    },
    siteName: '',
    getLocale: { start: false, success: false, fail: false },
    getEstimate: { start: false, success: false, fail: false },
    createAccount: {
        start: false, success: false, fail: false, error: undefined
    },
    updateAccount: { start: false, success: false, fail: false },
    xdid: '',
    templateId: null,
    modalOpen: false,
    lyp: {
        bedroomCount: 0,
        bathroomCount: 1
    },
    estimate: null,
    updateAccountData: null,
    haSessionV3Id: null,
    createPropertyError: null,
    createdPropertyTriad: null,
    expediaId: null,
    footerLinks: null,
    getFooterLinks: { start: false, success: false, fail: false },
    isLoggedIn: false,
    vrboOwnerAuthUrl: '',
    vrboGenericAuthUrl: '',
    loggedInUserType: '',
    hasAdvertiserForSite: false,
    fingerprint: {},
    hasFullyAuthenticated: false,
    updateAccountInProgress: false,
    aemData: {},
    features: {},
    pointOfSale: DEFAULT_POS,
    isInternalUser: false,
    pathSource: pathSources.ULX,
    isVacasaViewed: false,
    isModalOpen: false,
    displayBrand: '',
    userCountryCode: ''
};

const isPm = ({ answers }) => {
    const { propertyCount, isSameBuilding } = answers;
    return (propertyCount !== null && isSameBuilding !== null)
        ? (propertyCount > 5 && !isSameBuilding)
        : false;
};

export default (state = initialState, action) => {
    switch (action.type) {
    case AppActions.SET_ROOT_PATH:
        return { ...state, rootPath: action.rootPath };
    case AppActions.SET_TRANSLATIONS:
        return { ...state, translations: action.translations };
    case AppActions.SET_FALLBACK_TRANSLATIONS:
        return { ...state, fallbackTranslations: action.translations };
    case AppActions.SET_LOCALE:
        return { ...state, locale: { ...state.locale, canonical: action.locale } };
    case AppActions.SET_DISPLAY_LOCALE:
        return { ...state, locale: { ...state.locale, display: action.locale } };
    case AppActions.SET_LOCALE_LIST:
        return { ...state, locale: { ...state.locale, list: action.list } };
    case AppActions.SET_ISPM:
        return { ...state, isPm: action.isPm };
    case AppActions.SET_PROPERTY_COUNT:
        return { ...state, answers: { ...state.answers, propertyCount: action.propertyCount } };
    case AppActions.SET_PROPERTY_TYPE:
        return { ...state, answers: { ...state.answers, propertyType: action.propertyType } };
    case AppActions.SET_SOFTWARE_ID:
        return { ...state, answers: { ...state.answers, softwareId: action.softwareId } };
    case AppActions.SET_IS_SAME_BUILDING: {
        const newState = {
            ...state, answers: { ...state.answers, isSameBuilding: action.isSameBuilding }
        };
        return { ...newState, isPm: isPm(newState) };
    }
    case AppActions.SET_DESTINATION: {
        return { ...state, destination: action.destination };
    }
    case AppActions.SET_USES_SOFTWARE: {
        return { ...state, usesSoftware: action.usesSoftware };
    }
    case AppActions.SET_QUERY:
        return { ...state, query: action.query };
    case AppActions.SET_REFERRER:
        return { ...state, referrer: action.referrer };
    case AppActions.SET_BRAND:
        return { ...state, brand: action.brand };
    case AppActions.SET_DISPLAY_BRAND:
        return { ...state, displayBrand: action.displayBrand };
    case AppActions.SET_XDID:
        return { ...state, xdid: action.xdid };
    case AppActions.SET_SCROLLING:
        return { ...state, scrolling: action.scrolling };
    case AppActions.GET_LOCALE_START:
        return { ...state, getLocale: { start: true, success: false, fail: false } };
    case AppActions.GET_LOCALE_FAIL:
        return { ...state, getLocale: { start: false, success: false, fail: true } };
    case AppActions.GET_LOCALE_SUCCESS:
        return {
            ...state,
            getLocale: { start: false, success: true, fail: false },
            translations: action.localeData.translations,
            vrboOwnerAuthUrl: action.localeData.vrboOwnerAuthUrl,
            vrboGenericAuthUrl: action.localeData.vrboGenericAuthUrl
        };
    case AppActions.GET_ESTIMATE_START:
        return { ...state, getEstimate: { start: true, success: false, fail: false } };
    case AppActions.GET_ESTIMATE_FAIL:
        return { ...state, getEstimate: { start: false, success: false, fail: true } };
    case AppActions.GET_ESTIMATE_SUCCESS:
        return {
            ...state,
            getEstimate: { start: false, success: true, fail: false },
            estimate: action.estimate
        };
    case AppActions.CREATE_ACCOUNT_START:
        return {
            ...state,
            createAccount: {
                start: true, success: false, fail: false, error: undefined
            }
        };
    case AppActions.CREATE_ACCOUNT_FAIL:
        return {
            ...state,
            createAccount: {
                start: false, success: false, fail: true, error: action.error
            }
        };
    case AppActions.CREATE_ACCOUNT_SUCCESS:
        return {
            ...state,
            createAccount: {
                start: false, success: true, fail: false, error: undefined
            }
        };
    case AppActions.UPDATE_ACCOUNT_START:
        return { ...state, updateAccount: { start: true, success: false, fail: false } };
    case AppActions.UPDATE_ACCOUNT_FAIL:
        return { ...state, updateAccount: { start: false, success: false, fail: true } };
    case AppActions.UPDATE_ACCOUNT_SUCCESS:
        return {
            ...state,
            updateAccount: { start: false, success: true, fail: false },
            updateAccountData: action.updateAccountData.vrboContact
        };
    case AppActions.SET_EXPERIMENTS:
        return { ...state, experiments: action.experiments };
    case AppActions.SET_TEMPLATE_ID:
        return { ...state, templateId: action.templateId };
    case AppActions.SET_BATHROOM_COUNT: {
        const halfBathsAllowed = getLinks(state.locale.canonical, state.siteName).hasHalfBath;
        // Round down if we get a half bath from local storage but it isn't allowed
        const count = halfBathsAllowed ? action.count : Math.floor(action.count);
        // Don't round all the way to 0... set to 1 if we got that far
        return { ...state, lyp: { ...state.lyp, bathroomCount: count === 0 ? 1 : count } };
    }
    case AppActions.SET_BEDROOM_COUNT:
        return { ...state, lyp: { ...state.lyp, bedroomCount: action.count } };
    case AppActions.TOGGLE_MODAL_OPEN:
        return { ...state, modalOpen: action.modalOpen };
    case AppActions.SET_HA_SESSION_V3_ID:
        return { ...state, haSessionV3Id: action.sessionId };
    case AppActions.SET_CREATE_PROPERTY_ERROR:
        return { ...state, createPropertyError: action.errorMessage };
    case AppActions.SET_CREATED_PROPERTY_TRIAD:
        return { ...state, createdPropertyTriad: action.triad };
    case AppActions.SET_EXPEDIA_ID:
        return { ...state, expediaId: action.expediaId };
    case AppActions.GET_FOOTER_LINKS_START:
        return { ...state, getFooterLinks: { start: true, success: false, fail: false } };
    case AppActions.GET_FOOTER_LINKS_FAIL:
        return { ...state, getFooterLinks: { start: false, success: false, fail: true } };
    case AppActions.GET_FOOTER_LINKS_SUCCESS:
        return {
            ...state,
            getFooterLinks: { start: false, success: true, fail: false },
            footerLinks: action.links
        };
    case AppActions.SET_USER_LOGGED_IN:
        return { ...state, isLoggedIn: action.isLoggedIn };
    case AppActions.SET_VRBO_OWNER_AUTH_URL:
        return { ...state, vrboOwnerAuthUrl: action.vrboOwnerAuthUrl };
    case AppActions.SET_VRBO_GENERIC_AUTH_URL:
        return { ...state, vrboGenericAuthUrl: action.vrboGenericAuthUrl };
    case AppActions.SET_LOGGED_IN_USER_TYPE:
        return { ...state, loggedInUserType: action.loggedInUserType };
    case AppActions.SET_HAS_ADVERTISER_FOR_SITE:
        return { ...state, hasAdvertiserForSite: action.hasAdvertiser };
    case AppActions.SET_FINGERPRINT:
        return { ...state, fingerprint: action.fingerprint };
    case AppActions.SET_HAS_FULLY_AUTHENTICATED:
        return { ...state, hasFullyAuthenticated: action.hasFullyAuthenticated };
    case AppActions.SET_UPDATE_ACCOUNT_IN_PROGRESS:
        return { ...state, updateAccountInProgress: action.updateAccountInProgress };
    case AppActions.SET_SITENAME:
        return { ...state, siteName: action.siteName };
    case AppActions.SET_SITE_URLS:
        return { ...state, siteUrls: action.urls };
    case AppActions.SET_AEM_DATA:
        return { ...state, aemData: action.aemData };
    case AppActions.SET_FEATURES:
        return { ...state, features: action.features };
    case AppActions.SET_POINT_OF_SALE:
        return { ...state, pointOfSale: action.pos };
    case AppActions.SET_INTERNAL_USER:
        return { ...state, isInternalUser: action.isInternalUser };
    case AppActions.SET_PATH_SOURCE:
        return { ...state, pathSource: action.source };
    case AppActions.SET_VACASA_VIEWED:
        return { ...state, isVacasaViewed: action.isVacasaViewed };
    case AppActions.SET_IS_MODAL_OPEN:
        return { ...state, isModalOpen: action.isModalOpen };
    case AppActions.SET_USER_COUNTRY_CODE:
        return { ...state, userCountryCode: action.userCountryCode };
    default:
        return state;
    }
};
